import {
    getObjects,
    createObjects,
    updateObjects,
    deleteObjects,
} from './../api/webApplicationApi';
import { v4 as uuidv4 } from 'uuid';
import { Group } from './group';
import { UserGroup } from './userGroup';
import { SiteGroup } from './siteGroup';
import { Role } from './role';
import { Policy } from './policy';
import { Invitation } from './invitation';

const objectType = 'organization';

export class Organization {
    private _saveMethod = createObjects;
    private _object: omOrganization;
    private _lastError: Error;

    public constructor(organizationID: string) {
        this._object = {
            organizationID: organizationID,
            accountID: undefined,
            name: undefined,
            groups: [],
            userGroups: [],
            siteGroups: [],
            roles: [],
            policies: [],
            invitations: [],
        };
    }

    public static createNew(): Organization {
        return new Organization(uuidv4());
    }

    public static createFromObject(organization: omOrganization): Organization {
        const obj = new Organization(organization.organizationID);
        obj._object = organization;
        obj._saveMethod = updateObjects;
        return obj;
    }

    public async load(): Promise<boolean> {
        const getObject: GetRequest = {
            objectType: objectType,
            keys: [this._object.organizationID],
        };
        this._lastError = undefined;
        try {
            const response = await getObjects([getObject]);
            if (
                response &&
                response.length > 0 &&
                response[0].objects &&
                response[0].objects.length == 1
            ) {
                this._saveMethod = updateObjects;
                this._object = response[0].objects[0];
                return true;
            } else {
                this._lastError = new Error('organization not found');
                return false;
            }
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async save(): Promise<boolean> {
        const saveObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;

        if (this._object.name == undefined) {
            this._lastError = new Error('Name is undefined');
            return false;
        }
        if (this._object.accountID == undefined) {
            this._lastError = new Error('Account ID is undefined');
            return false;
        }
        try {
            const response = await this._saveMethod([saveObject]);
            if (response) {
                this._saveMethod = updateObjects;
            } else {
                this._lastError = new Error('unknown server error');
            }
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async delete(): Promise<boolean> {
        const deleteObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;
        try {
            const response = await deleteObjects([deleteObject]);
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public get lastError(): Error {
        return this._lastError;
    }

    public get organizationId(): string {
        return this._object.organizationID;
    }

    public get accountID(): string {
        return this._object.accountID;
    }

    public set accountID(accountID: string) {
        this._object.accountID = accountID;
    }

    public get name(): string {
        return this._object.name;
    }

    public set name(name: string) {
        this._object.name = name;
    }

    public get groups(): string[] {
        return this._object.groups;
    }

    public addGroup(group: Group): void {
        if (this._object.groups.includes(group.groupID)) return;
        this._object.groups.push(group.groupID);
    }

    public deleteGroup(group: Group): void {
        const index = this._object.groups.indexOf(group.groupID);
        if (index > -1) {
            this._object.groups.splice(index, 1);
        }
    }

    public get userGroups(): string[] {
        return this._object.userGroups;
    }

    public addUserGroup(userGroup: UserGroup): void {
        if (this._object.userGroups.includes(userGroup.userGroupID)) return;
        this._object.userGroups.push(userGroup.userGroupID);
    }

    public deleteUserGroup(userGroup: UserGroup): void {
        const index = this._object.userGroups.indexOf(userGroup.userGroupID);
        if (index > -1) {
            this._object.userGroups.splice(index, 1);
        }
    }

    public get siteGroups(): string[] {
        return this._object.siteGroups;
    }

    public addSiteGroup(siteGroup: SiteGroup): void {
        if (this._object.siteGroups.includes(siteGroup.siteGroupID)) return;
        this._object.siteGroups.push(siteGroup.siteGroupID);
    }

    public deleteSiteGroup(siteGroup: SiteGroup): void {
        const index = this._object.siteGroups.indexOf(siteGroup.siteGroupID);
        if (index > -1) {
            this._object.siteGroups.splice(index, 1);
        }
    }

    public get roles(): string[] {
        return this._object.roles;
    }

    public addRole(role: Role): void {
        if (this._object.roles.includes(role.roleID)) return;
        this._object.roles.push(role.roleID);
    }

    public deleteRole(role: Role): void {
        const index = this._object.roles.indexOf(role.roleID);
        if (index > -1) {
            this._object.roles.splice(index, 1);
        }
    }

    public get policies(): string[] {
        return this._object.policies;
    }

    public addPolicy(policy: Policy): void {
        if (this._object.policies.includes(policy.policyID)) return;
        this._object.policies.push(policy.policyID);
    }

    public deletePolicy(policy: Policy): void {
        const index = this._object.policies.indexOf(policy.policyID);
        if (index > -1) {
            this._object.policies.splice(index, 1);
        }
    }

    public get invitations(): string[] {
        return this._object.invitations;
    }

    public addInvitation(invitation: Invitation): void {
        if (this._object.invitations.includes(invitation.invitationID)) return;
        this._object.invitations.push(invitation.invitationID);
    }

    public deleteInvitation(invitation: Invitation): void {
        const index = this._object.invitations.indexOf(invitation.invitationID);
        if (index > -1) {
            this._object.invitations.splice(index, 1);
        }
    }
}
